import { Reducer } from "redux";
import {
  SystemEmailTemplatesAction,
  SYSTEM_EMAIL_TEMPLATES_ERROR_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_CONFIG_START_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_CONFIG_SUCCESS_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_LIST_START_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_LIST_SUCCESS_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_TEMPLATE_START_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_FETCH_TEMPLATE_SUCCESS_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_RESET_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_SAVE_START_ACTION_TYPE,
  SYSTEM_EMAIL_TEMPLATES_SAVE_SUCCESS_ACTION_TYPE
} from "actions/systemEmailTemplates/SystemEmailTemplatesActionsTypes";
import { SystemEmailTemplatesState, SystemEmailTemplatesStatus } from "./SystemEmailTemplatesState";

const initialState: SystemEmailTemplatesState = {
  list: [],
  currentTemplate: undefined,
  unlayerConfig: undefined,
  loadingList: false,
  loadingTemplate: false,
  loadingConfig: false,
  saving: false,
  status: SystemEmailTemplatesStatus.Initial
};

/**
 * RESET only wipes per-session state — `unlayerConfig` survives because it
 * doesn't change between opens of the editor; refetching on every open would
 * add a round-trip every time the user clicks Create / Edit.
 */
export const systemEmailTemplatesReducer: Reducer<
  SystemEmailTemplatesState,
  SystemEmailTemplatesAction
> = (state = initialState, action) => {
  switch (action.type) {
    case SYSTEM_EMAIL_TEMPLATES_RESET_ACTION_TYPE:
      return {
        ...state,
        currentTemplate: undefined,
        saving: false,
        status: SystemEmailTemplatesStatus.Initial
      };

    case SYSTEM_EMAIL_TEMPLATES_FETCH_LIST_START_ACTION_TYPE:
      return {
        ...state,
        loadingList: true,
        status: SystemEmailTemplatesStatus.FetchingList
      };

    case SYSTEM_EMAIL_TEMPLATES_FETCH_LIST_SUCCESS_ACTION_TYPE:
      return {
        ...state,
        list: action.payload.list,
        loadingList: false,
        status: SystemEmailTemplatesStatus.FetchListSuccess
      };

    case SYSTEM_EMAIL_TEMPLATES_FETCH_TEMPLATE_START_ACTION_TYPE:
      return {
        ...state,
        currentTemplate: undefined,
        loadingTemplate: true,
        status: SystemEmailTemplatesStatus.FetchingTemplate
      };

    case SYSTEM_EMAIL_TEMPLATES_FETCH_TEMPLATE_SUCCESS_ACTION_TYPE:
      return {
        ...state,
        currentTemplate: action.payload.template,
        loadingTemplate: false,
        status: SystemEmailTemplatesStatus.FetchTemplateSuccess
      };

    case SYSTEM_EMAIL_TEMPLATES_FETCH_CONFIG_START_ACTION_TYPE:
      return {
        ...state,
        loadingConfig: true,
        status: SystemEmailTemplatesStatus.FetchingConfig
      };

    case SYSTEM_EMAIL_TEMPLATES_FETCH_CONFIG_SUCCESS_ACTION_TYPE:
      return {
        ...state,
        unlayerConfig: action.payload.config,
        loadingConfig: false,
        status: SystemEmailTemplatesStatus.FetchConfigSuccess
      };

    case SYSTEM_EMAIL_TEMPLATES_SAVE_START_ACTION_TYPE:
      return {
        ...state,
        saving: true,
        status: SystemEmailTemplatesStatus.Saving
      };

    case SYSTEM_EMAIL_TEMPLATES_SAVE_SUCCESS_ACTION_TYPE: {
      // Splice the saved template into the list so the table stays in sync without
      // a refetch round-trip. Replaces the existing row by id, otherwise inserts
      // at the top.
      const incoming = action.payload.template;
      const exists = state.list.some(t => t.id === incoming.id);
      const nextList = exists
        ? state.list.map(t => (t.id === incoming.id ? incoming : t))
        : [incoming, ...state.list];
      return {
        ...state,
        list: nextList,
        currentTemplate: incoming,
        saving: false,
        status: SystemEmailTemplatesStatus.SaveSuccess
      };
    }

    case SYSTEM_EMAIL_TEMPLATES_ERROR_ACTION_TYPE:
      return {
        ...state,
        loadingList: false,
        loadingTemplate: false,
        loadingConfig: false,
        saving: false,
        status: SystemEmailTemplatesStatus.Error
      };

    default:
      return state;
  }
};

export default systemEmailTemplatesReducer;
